import React, { createContext, useState, useContext, useEffect } from 'react';
import Cookies from 'js-cookie';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';

const CartContext = createContext();

export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [anonymousId, setAnonymousId] = useState(null);

  useEffect(() => {
    let id = Cookies.get('anonymousId');
    if (!id) {
      id = uuidv4();
      Cookies.set('anonymousId', id, { expires: 1 });
    }
    setAnonymousId(id);

    const loadCartItems = async () => {
      const savedCartItems = Cookies.get(id);
      if (savedCartItems) {
        try {
          const parsedItems = JSON.parse(savedCartItems);
    
          // Fetch product details for each item
          const productIds = parsedItems.map(item => item.id);
          const products = await fetchProductsByIds(productIds);
    
          const cartItemsWithQuantities = products.map(product => {
            const savedItem = parsedItems.find(item => item.id === product.id);
            return { ...product, quantity: savedItem ? savedItem.quantity : 1 };
          });
    
          setCartItems(cartItemsWithQuantities || []);
        } catch (error) {
          console.error('Error loading cart items:', error);
        }
      }
    };
    

    if (id) loadCartItems();
  }, []);

  const fetchProductsByIds = async (ids) => {
    try {
      const productPromises = ids.map(id =>
        axios.get(`https://api.theonlysale.com/api/product/get/${id}`)
      );
  
      const responses = await Promise.all(productPromises);
  
      const products = responses.map(response => {
        const productData = response.data;
  
        let imagesArray = [];
        try {
          imagesArray = JSON.parse(productData.images);
        } catch (e) {
          console.error("Error parsing images array:", e);
        }
  
        const imageURIs = imagesArray.map(image =>
          `https://api.theonlysale.com/uploads/images/${image.split(/[\\/]/).pop()}`
        );
  
        return { ...productData, imageURIs };
      });
  
      return products;
    } catch (error) {
      console.error('Error fetching product details:', error);
      return [];
    }
  };
  

  useEffect(() => {
    if (anonymousId) {
      const cartItemsToSave = cartItems.map(item => ({
        id: item.id,
        quantity: item.quantity,
      }));
      Cookies.set(anonymousId, JSON.stringify(cartItemsToSave), { expires: 1 });
    }
  }, [cartItems, anonymousId]);
  

  function addToCart(newItem) {
    setCartItems((prevItems) => {
      const existingItem = prevItems.find(item => item.id === newItem.id);
      if (existingItem) {
        return prevItems.map(item =>
          item.id === newItem.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      } else {
        return [...prevItems, { ...newItem, quantity: 1 }];
      }
    });
  }
  
  const removeFromCart = (productIdToRemove) => {
    setCartItems((prevItems) =>
      prevItems.filter(item => item.id !== productIdToRemove)
    );
  };

  function increaseQuantity(id) {
    setCartItems(prevItems =>
      prevItems.map(item =>
        item.id === id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      )
    );
  }
  
  function decreaseQuantity(id) {
    setCartItems(prevItems =>
      prevItems.map(item =>
        item.id === id && item.quantity > 1
          ? { ...item, quantity: item.quantity - 1 }
          : item
      )
    );
  }
  
  function calculateTotal() {
    return cartItems.reduce((total, item) => total + item.final_price * item.quantity, 0);
  }
  
  const clearCart = () => {
    setCartItems([]);
    if (anonymousId) {
      Cookies.remove(anonymousId);
    }
  };
  const contextValue = {
    cartItems,
    addToCart,
    removeFromCart,
    increaseQuantity,
    decreaseQuantity,
    clearCart,
    calculateTotal,
  };

  return (
    <CartContext.Provider value={contextValue}>
      {children}
    </CartContext.Provider>
  );
};
